import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Switch,
  DatePicker,
  Divider,
  message,
  Spin,
  Space,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { getMachine, createMachine, updateMachine, getVendors } from "../../api/machineApi";
import PageTitle from "../page-header/PageHeader";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;

const MACHINE_TYPES = [
  { value: "corrugation", label: "Corrugation" },
  { value: "die_cutting", label: "Die Cutting (Casting)" },
  { value: "rotary", label: "Rotary" },
  { value: "slotter", label: "Slotter" },
  { value: "offset", label: "Offset Printing" },
  { value: "flexo", label: "Flexo Printing" },
  { value: "pasting", label: "Pasting" },
  { value: "sheet_cutter", label: "Sheet Cutter" },
  { value: "stitching", label: "Stitching" },
  { value: "glueing", label: "Glueing" },
  { value: "lamination", label: "Lamination" },
];

const MACHINE_SUBTYPES = {
  corrugation: [
    { value: "2_ply", label: "2 Ply" },
    { value: "3_ply", label: "3 Ply" },
    { value: "5_ply", label: "5 Ply" },
    { value: "7_ply", label: "7 Ply" },
  ],
  die_cutting: [
    { value: "flat_bed", label: "Flat Bed" },
    { value: "rotary", label: "Rotary" },
    { value: "semi_auto", label: "Semi Auto" },
    { value: "auto", label: "Auto" },
  ],
  pasting: [
    { value: "semi_auto", label: "Semi Auto" },
    { value: "auto", label: "Auto" },
  ],
  offset: [
    { value: "single_color", label: "Single Color" },
    { value: "multi_color", label: "Multi Color" },
  ],
  flexo: [
    { value: "single_color", label: "Single Color" },
    { value: "multi_color", label: "Multi Color" },
  ],
  sheet_cutter: [
    { value: "rotary", label: "Rotary" },
    { value: "flat", label: "Flat" },
  ],
  lamination: [
    { value: "thermal", label: "Thermal" },
    { value: "water_based", label: "Water Based" },
  ],
};

const MATERIAL_OPTIONS = ["KRAFT", "DUPLEX", "SEMI", "WHITE_TOP", "COATED"];
const FLUTE_OPTIONS = ["B", "C", "E", "BC", "EB", "F"];
const STAGE_OPTIONS = [
  "CORRUGATION",
  "SHEET_CUTTING",
  "PASTING",
  "PRINTING",
  "CASTING",
  "ROTARY",
  "SLOTTING",
  "JOINING",
  "STITCHING",
  "GLUEING",
  "LAMINATION",
];

const MachineForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [isInhouse, setIsInhouse] = useState(true);
  const [machineType, setMachineType] = useState(null);
  const isEdit = Boolean(id && id !== "new");

  useEffect(() => {
    fetchVendors();
    if (isEdit) {
      fetchMachine();
    }
  }, [id]);

  const fetchVendors = async () => {
    try {
      const data = await getVendors();
      setVendors(data);
    } catch (error) {
      console.error("Failed to fetch vendors", error);
    }
  };

  const fetchMachine = async () => {
    try {
      setLoading(true);
      const data = await getMachine(id);
      setIsInhouse(data.is_inhouse !== false);
      setMachineType(data.type || null);
      form.setFieldsValue({
        ...data,
        purchase_date: data.purchase_date ? moment(data.purchase_date) : null,
        next_maintenance: data.next_maintenance ? moment(data.next_maintenance) : null,
      });
    } catch (error) {
      message.error("Failed to fetch machine details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      
      const payload = {
        ...values,
        purchase_date: values.purchase_date?.toISOString(),
        next_maintenance: values.next_maintenance?.toISOString(),
        // Clear vendor_id if inhouse, clear location if outsourced
        vendor_id: values.is_inhouse ? null : values.vendor_id,
        location: values.is_inhouse ? values.location : null,
      };

      if (isEdit) {
        await updateMachine(id, payload);
        message.success("Machine updated successfully");
      } else {
        await createMachine(payload);
        message.success("Machine created successfully");
      }
      navigate("/machine");
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to save machine");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <PageTitle title="Back" subtitle={isEdit ? "Edit Machine" : "Add New Machine"} />

      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/machine")}>
          Back to List
        </Button>
      </Space>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            is_active: true,
            is_inhouse: true,
            status: "operational",
            unit: "inches",
            maintenance_interval_days: 30,
          }}
        >
          {/* Basic Information */}
          <Divider orientation="left">Basic Information</Divider>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="code"
                label="Machine Code"
                rules={[{ required: true, message: "Please enter machine code" }]}
              >
                <Input placeholder="e.g., CORR-52, DIE-40x28" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="name"
                label="Machine Name"
                rules={[{ required: true, message: "Please enter machine name" }]}
              >
                <Input placeholder="e.g., Corrugation 52 inch" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="type"
                label="Machine Type"
                rules={[{ required: true, message: "Please select type" }]}
              >
                <Select
                  placeholder="Select type"
                  onChange={(val) => {
                    setMachineType(val);
                    form.setFieldsValue({ subtype: undefined });
                  }}
                >
                  {MACHINE_TYPES.map((t) => (
                    <Option key={t.value} value={t.value}>
                      {t.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="subtype" label="Machine Subtype">
                <Select
                  placeholder={machineType ? "Select subtype" : "Select machine type first"}
                  allowClear
                  disabled={!machineType || !MACHINE_SUBTYPES[machineType]}
                >
                  {(MACHINE_SUBTYPES[machineType] || []).map((st) => (
                    <Option key={st.value} value={st.value}>
                      {st.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="is_inhouse" label="Inhouse" valuePropName="checked">
                <Switch 
                  checkedChildren="Inhouse" 
                  unCheckedChildren="Outsource" 
                  onChange={(checked) => setIsInhouse(checked)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              {isInhouse ? (
                <Form.Item name="location" label="Location">
                  <Input placeholder="e.g., Production Floor, Unit 2" />
                </Form.Item>
              ) : (
                <Form.Item 
                  name="vendor_id" 
                  label="Vendor / Supplier"
                  rules={[{ required: !isInhouse, message: "Please select a vendor" }]}
                >
                  <Select 
                    placeholder="Select vendor" 
                    allowClear 
                    showSearch
                    optionFilterProp="children"
                  >
                    {vendors.map((v) => (
                      <Option key={v.id} value={v.id}>
                        {v.name} {v.phone ? `(${v.phone})` : ""}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="operational">Operational</Option>
                  <Option value="maintenance">Under Maintenance</Option>
                  <Option value="breakdown">Breakdown</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Dimensions & Speed */}
          <Divider orientation="left">Dimensions & Capacity</Divider>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="width" label="Width (Max)">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  step={0.5}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="height" label="Height/Length (Max)">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  step={0.5}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="min_width" label="Min Width">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  step={0.5}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="min_length" label="Min Length">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                  step={0.5}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="unit" label="Unit">
                <Select>
                  <Option value="inches">Inches</Option>
                  <Option value="cm">Centimeters</Option>
                  <Option value="mm">Millimeters</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="speed_sheets_per_hour" label="Speed (Sheets/Hour)">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="speed_meters_per_min" label="Speed (Meters/Min)">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="0"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Compatibility */}
          <Divider orientation="left">Compatibility</Divider>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="compatible_materials" label="Compatible Materials">
                <Select mode="multiple" placeholder="Select materials">
                  {MATERIAL_OPTIONS.map((m) => (
                    <Option key={m} value={m}>
                      {m}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="compatible_flutes" label="Compatible Flutes">
                <Select mode="multiple" placeholder="Select flutes">
                  {FLUTE_OPTIONS.map((f) => (
                    <Option key={f} value={f}>
                      {f}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="applicable_stages" label="Applicable Stages">
                <Select mode="multiple" placeholder="Select stages">
                  {STAGE_OPTIONS.map((s) => (
                    <Option key={s} value={s}>
                      {s}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Maintenance */}
          <Divider orientation="left">Maintenance</Divider>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="maintenance_interval_days" label="Maintenance Interval (Days)">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="30"
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="next_maintenance" label="Next Scheduled Maintenance">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Machine Details */}
          <Divider orientation="left">Machine Details (Optional)</Divider>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="manufacturer" label="Manufacturer">
                <Input placeholder="e.g., XYZ Manufacturing" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="model_number" label="Model Number">
                <Input placeholder="e.g., MODEL-2023-A" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="purchase_date" label="Purchase Date">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Status Reason */}
          {isEdit && (
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="status_reason" label="Status Reason (if not operational)">
                  <TextArea rows={2} placeholder="Reason for current status" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Submit */}
          <Divider />
          <Row>
            <Col span={24} style={{ textAlign: "right" }}>
              <Space>
                <Button onClick={() => navigate("/machine")}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={submitting}
                >
                  {isEdit ? "Update Machine" : "Create Machine"}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
};

export default MachineForm;
